/**
 * FornadaCustosAdicionaisManager Component - Gerenciador de custos avulsos
 */

import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { FornadaCustoAdicionalDialog } from "./FornadaCustoAdicionalDialog";
import type { FornadaCustoAdicional } from "@/types/fornada";

interface FornadaCustosAdicionaisManagerProps {
  custos: FornadaCustoAdicional[];
  onCustosChange: (custos: FornadaCustoAdicional[]) => void;
}

export function FornadaCustosAdicionaisManager({ custos, onCustosChange }: FornadaCustosAdicionaisManagerProps) {
  const [showCustoDialog, setShowCustoDialog] = useState(false);
  const [editingCusto, setEditingCusto] = useState<FornadaCustoAdicional | null>(null);

  const handleAddCusto = () => {
    setEditingCusto(null);
    setShowCustoDialog(true);
  };

  const handleEditCusto = (custo: FornadaCustoAdicional) => {
    setEditingCusto(custo);
    setShowCustoDialog(true);
  };

  const handleDeleteCusto = (id: string) => {
    onCustosChange(custos.filter(custo => custo.id_fornada_custo_adicional !== id));
  };

  const handleSaveCusto = (custo: FornadaCustoAdicional) => {
    if (editingCusto) {
      onCustosChange(custos.map(c => c.id_fornada_custo_adicional === custo.id_fornada_custo_adicional ? custo : c));
    } else {
      onCustosChange([...custos, { ...custo, id_fornada_custo_adicional: crypto.randomUUID() }]);
    }
    setShowCustoDialog(false);
  };

  const totalCustosAdicionais = custos.reduce((sum, custo) => sum + custo.valor_total, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Itens Avulsos</h3>
          <p className="text-sm text-muted-foreground">
            Embalagens, insumos extras, etc.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={handleAddCusto}>
          <Plus className="w-4 h-4 mr-2" />
          Adicionar Item
        </Button>
      </div>

      {custos.length > 0 && (
        <>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Descrição</TableHead>
                  <TableHead className="text-right">Quantidade</TableHead>
                  <TableHead className="text-right">Valor Unit.</TableHead>
                  <TableHead className="text-right">Valor Total</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {custos.map((custo) => (
                  <TableRow key={custo.id_fornada_custo_adicional}>
                    <TableCell>{custo.descricao}</TableCell>
                    <TableCell className="text-right">{custo.quantidade}</TableCell>
                    <TableCell className="text-right">
                      R$ {custo.valor_unitario.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      R$ {custo.valor_total.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          type="button"
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleEditCusto(custo)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          type="button"
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDeleteCusto(custo.id_fornada_custo_adicional!)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex justify-between items-center p-3 bg-muted rounded-md">
            <span className="font-semibold">Total Itens Avulsos:</span>
            <span className="text-lg font-bold">
              {new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(totalCustosAdicionais)}
            </span>
          </div>
        </>
      )}

      <FornadaCustoAdicionalDialog
        open={showCustoDialog}
        onOpenChange={setShowCustoDialog}
        item={editingCusto}
        onSave={handleSaveCusto}
      />
    </div>
  );
}
